import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from "react-router-dom";
import { Button, Card, Empty, Progress, Space, Spin, Typography, message } from 'antd';
import { ImportOutlined } from '@ant-design/icons';
import { getIndexStatus, retrieveGraph, importGraphDirect } from '../services/api';
import { JobTab } from '../components/JobTab';

const JOB_STATUS_POLL_INTERVAL_MS = 5000;
const ACTIVE_JOB_STATUSES = new Set(['scheduled', 'running', 'importing']);

export default function JobsPage({ setGraphId }) {
  //todo: implement the case where a specific graph is passed.
  const { graphId = null } = useParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importProgress, setImportProgress] = useState(null); // null | {phase:'upload'|'restore', percent}
  const fileInputRef = useRef(null);

  const fetchJobs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      let nextJobs = []
      if (graphId) {
        nextJobs = await retrieveGraph(graphId);
        console.log(nextJobs);
      } else {
        nextJobs = await getIndexStatus();
      }
      if (!Array.isArray(nextJobs)) {
        throw new Error('Invalid jobs data');
      }
      setJobs(nextJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (!silent) message.error('Failed to fetch jobs. Please try again later.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
      fetchJobs();
  }, [fetchJobs]);

  const hasActiveJobs = jobs.some((job) => ACTIVE_JOB_STATUSES.has(job.status));

  useEffect(() => {
    if (!hasActiveJobs) return undefined;

    let cancelled = false;
    let timeoutId;

    const pollJobs = async () => {
      if (document.visibilityState !== 'hidden') {
        await fetchJobs({ silent: true });
      }
      if (!cancelled) {
        timeoutId = window.setTimeout(pollJobs, JOB_STATUS_POLL_INTERVAL_MS);
      }
    };

    timeoutId = window.setTimeout(pollJobs, JOB_STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [fetchJobs, hasActiveJobs]);

  const doImport = async (file) => {
    setImportProgress({ phase: 'upload', percent: 0 });
    try {
      const result = await importGraphDirect(file, (loaded, total) => {
        const percent = total ? Math.round((loaded / total) * 100) : 0;
        if (percent >= 100) {
          setImportProgress({ phase: 'commit', percent: 100 });
        } else {
          setImportProgress({ phase: 'upload', percent });
        }
      });
      const warnings = (result?.warnings || []);
      if (warnings.length) {
        warnings.forEach((w) => message.warning(w));
      }
      message.success(`Import started for graph #${result?.id}. Progress will appear in the Jobs list.`);
      await fetchJobs();
    } catch (error) {
      console.error('Import failed:', error);
      message.error(error.message || 'Import failed.');
    } finally {
      setImportProgress(null);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    await doImport(file);
  };

  return (
    <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
          <Button
            icon={<ImportOutlined />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importProgress !== null}
          >
            Import graph
          </Button>
        </div>
        {importProgress && (
          <Card size="small">
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Typography.Text>
                {importProgress.phase === 'upload'
                  ? 'Uploading zip...'
                  : 'Finalising...'}
              </Typography.Text>
              <Progress
                percent={importProgress.percent}
                status={importProgress.phase === 'commit' ? 'active' : 'normal'}
              />
            </Space>
          </Card>
        )}
        {loading ? (
          <div className="jobs-loading-state">
            <Spin size="large" />
          </div>
        ) : jobs.length === 0 ? (
          <Empty description="No indexing jobs found yet." />
        ) : (
          <div className="jobs-grid">
            {jobs.map((job) => (
              <div key={job.id} className="jobs-grid__item">
                <JobTab job={job} setGraphId={setGraphId} onRefresh={fetchJobs} setJobs={setJobs} />
              </div>
            ))}
          </div>
        )}
    </Space>
  );
}
